import { Box, ButtonBase, useTheme } from "@mui/material";
import { DotLegalPaper } from "@dotlegal/dotlegal-ui-components";
import { createSxStyles } from "@common/createSxStyles";

export interface SegmentedControlOption<T extends string | number> {
    label: string;
    value: T;
}

interface SegmentedControlProps<T extends string | number> {
    options: Array<SegmentedControlOption<T>>;
    selected: T;
    onSelect: (value: T) => void;
    ariaLabel?: string;
    disabled?: boolean;
    fullWidth?: boolean;
}

// A compact segmented choice between a few mutually exclusive options, framed in a DotLegalPaper. Used both to
// switch between the views of a page and to pick a mode inside a form.
function SegmentedControl<T extends string | number>({ options, selected, onSelect, ariaLabel, disabled, fullWidth }: SegmentedControlProps<T>) {
    const styles = useStyles();

    return (
        <DotLegalPaper removePadding sx={fullWidth ? styles.fullWidthPaper : styles.paper}>
            <Box role="group" aria-label={ariaLabel} sx={styles.group}>
                {options.map((option) => (
                    <ButtonBase
                        key={option.value}
                        aria-pressed={option.value === selected}
                        disabled={disabled}
                        sx={[styles.button, !!fullWidth && styles.fullWidthButton, option.value === selected && styles.selected]}
                        onClick={() => onSelect(option.value)}
                    >
                        {option.label}
                    </ButtonBase>
                ))}
            </Box>
        </DotLegalPaper>
    );
}

const useStyles = () => {
    const theme = useTheme();
    const focusRing = {
        boxShadow: `0 0 0 3px ${theme.palette.primary.main}`,
    };
    return createSxStyles({
        paper: {
            width: "fit-content",
            alignSelf: "flex-start",
        },
        fullWidthPaper: {
            width: "100%",
        },
        fullWidthButton: {
            flex: 1,
        },
        group: {
            display: "flex",
            gap: theme.spacing(0.5),
            padding: theme.spacing(0.75),
        },
        button: {
            paddingX: theme.spacing(2.5),
            // Makes the whole control 38.5px high, the height of a medium DotLegalButton, so it lines up with the page header buttons
            paddingY: "5.75px",
            borderRadius: "14px",
            fontWeight: theme.typography.fontWeightBold,
            fontSize: "0.85rem",
            color: theme.palette.grey[500],
            transition: "background-color 0.15s ease, color 0.15s ease",
            "&:hover": {
                backgroundColor: theme.palette.grey[100],
            },
            "&.Mui-focusVisible": focusRing,
            "&.Mui-disabled": {
                opacity: 0.6,
            },
        },
        selected: {
            backgroundColor: theme.palette.primary.light,
            color: theme.palette.primary.dark,
            outline: "2px solid " + theme.palette.primary.main,
            outlineOffset: "-2px",
            "&:hover": {
                backgroundColor: theme.palette.primary.light,
            },
        },
    });
};

export default SegmentedControl;
